/**
 * Phase REVEAL : sequence automatique, un tableau toutes les REVEAL_TIME
 * secondes. L'index courant est derive de l'heure serveur, donc tous les
 * onglets regardent le meme tableau au meme moment.
 *
 * Chaque item : zoom d'entree sur la zone puis pan/zoom LIBRES, lisere or,
 * curseur original/retouche, points, reactions emoji. Le curseur a sa propre
 * poignee : le glisser sur le canvas deplace la vue, le glisser sur la poignee
 * deplace la separation. Les deux ne se marchent donc jamais dessus.
 *
 * Sous la scene, une pellicule montre les retouches de TOUS les joueurs en
 * meme temps : cliquer sur une miniature l'agrandit dans la scene et fige le
 * defile. La phase se termine par REVEAL_BROWSE_MS de consultation libre :
 * c'est la seule occasion de regarder le travail des autres, elle ne peut pas
 * se limiter au temps du defile.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  REACTIONS,
  REVEAL_BROWSE_MS,
  REVEAL_TIME_MS,
  decodeDrawing,
  isCollab,
  isContest,
  isElimination,
  revealDurationMs,
  revealIndexAt,
  revealParadeMs,
  type RevealItem,
  type RoomSnapshot,
} from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { connection } from '../net/connection.js';
import { playerById, useRoomStore } from '../store/roomStore.js';
import { loadArtwork } from '../game/artwork.js';
import { HuntBoard } from '../game/HuntBoard.js';
import { ContestBoard } from '../game/ContestBoard.js';
import { CollabBoard } from '../game/CollabBoard.js';
import { RevealEngine } from '../game/RevealEngine.js';
import { renderThumbnail } from '../gallery/renderArtwork.js';
import { Avatar } from '../ui/Avatar.js';
import { GameLayout } from '../ui/GameLayout.js';
import { PhaseTimer } from '../ui/PhaseTimer.js';
import { Screen } from '../ui/Screen.js';
import { roundLabel } from '../ui/roundLabel.js';
import { observeSize } from '../game/canvas/observeSize.js';

/**
 * Duree du zoom d'entree sur la zone. Une fraction franche du temps d'affichage :
 * l'animation doit etre finie bien avant qu'on passe au tableau suivant, sinon
 * on ne voit jamais l'image stabilisee.
 */
const INTRO_MS = Math.round(REVEAL_TIME_MS * 0.3);
/** Aller-retour automatique du curseur, juste apres l'entree. */
const AUTO_SLIDE_MS = 1100;
/** Demi-largeur de la zone de prise du curseur, en px. */
const SPLIT_GRAB_PX = 16;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function RevealScreen({ room }: { room: RoomSnapshot }): ReactElement {
  const t = useT();
  const reveal = useRoomStore((state) => state.reveal);
  const hunt = useRoomStore((state) => state.hunt);
  const contest = useRoomStore((state) => state.contest);
  const collab = useRoomStore((state) => state.collab);
  const playerId = useRoomStore((state) => state.playerId);

  const [index, setIndex] = useState(0);
  /** Miniature choisie a la main : elle prend le pas sur le defile automatique. */
  const [pinned, setPinned] = useState<number | null>(null);

  const round = reveal?.round ?? null;
  useEffect(() => {
    setPinned(null);
  }, [round]);

  // L'index courant se deduit du temps restant : pas de compteur local qui
  // pourrait deriver entre onglets.
  useEffect(() => {
    if (reveal === null || room.endsAt === null) return;
    const total = reveal.items.length;
    const duration = revealDurationMs(total);
    const parade = revealParadeMs(total);
    // Pendant la consultation libre, la scene se pose sur SA PROPRE retouche.
    // Le defile donne le meme temps a tout le monde ; le rabiot, lui, n'a pas a
    // favoriser la retouche de quelqu'un d'autre, et c'est la sienne qu'on veut
    // regarder a ce moment-la. N'importe quelle miniature reste cliquable.
    const own = reveal.items.findIndex((item) => item.drawerId === playerId);
    const settled = own >= 0 ? own : total - 1;

    let raf = 0;
    const frame = (): void => {
      const remaining = connection.remaining(room.endsAt);
      const elapsed = duration - remaining;
      const next = elapsed < parade ? revealIndexAt(elapsed, total) : settled;
      setIndex((current) => (current === next ? current : next));
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [reveal, room.endsAt, playerId]);

  // Battle royale : la chasse a deja tout montre, en direct. La revelation ne
  // rejoue rien, elle laisse le tableau commun a l'ecran, fige, le temps de
  // voir enfin ou tout le monde s'etait cache.
  // Concours : on rejoue le meme plateau, mais les noms sortent et les voix
  // s'affichent. Il ouvre sur la retouche gagnante.
  // Collaboratif : le meme plateau, mais les voix sont sorties. C'etait un
  // ECRAN VIDE : la revelation de ce mode n'a ni `hunt` ni `reveal` a montrer,
  // et sans branche dediee il ne restait que le titre « Revelation ».
  if (isCollab(room.settings.mode) && collab !== null) {
    return (
      <Screen name="reveal-collab">
        <GameLayout room={room} selfId={playerId}>
          <div className="guessing">
            <header className="guessing__header">
              <strong className="board__round">{t('collab.boards')}</strong>
              <span>{t('collab.results')}</span>
            </header>
            <CollabBoard
              room={room}
              boards={collab.boards}
              drawings={collab.drawings}
              selfId={playerId}
              mine={collab.mine}
              tally={collab.tally ?? []}
              onVote={() => undefined}
            />
            <ReactionBar />
            <FloatingReactions room={room} />
          </div>
        </GameLayout>
      </Screen>
    );
  }

  if (isContest(room.settings.mode) && hunt !== null) {
    return (
      <Screen name="reveal-contest">
        <GameLayout room={room} selfId={playerId}>
          <div className="guessing">
            <header className="guessing__header">
              <strong className="board__round">{roundLabel(t, room, hunt.round)}</strong>
              <span>{t('contest.results')}</span>
            </header>
            <ContestBoard
              key={`reveal-${hunt.round}`}
              room={room}
              file={hunt.file}
              targets={hunt.targets}
              drawings={hunt.drawings}
              selfId={playerId}
              myVote={contest.mine}
              tally={contest.tally ?? {}}
              durationMs={REVEAL_BROWSE_MS}
              onVote={() => undefined}
            />
            <ReactionBar />
            <FloatingReactions room={room} />
          </div>
        </GameLayout>
      </Screen>
    );
  }

  if (hunt !== null) {
    return (
      <Screen name="reveal-hunt">
        <GameLayout room={room} selfId={playerId}>
          <div className="guessing">
            <header className="guessing__header">
              <strong className="board__round">{roundLabel(t, room, hunt.round)}</strong>
              <span>{t('hunt.subtitle')}</span>
              {/* Battle royale, manche blanche : aucune retouche trouvee, donc
                  aucun joueur sorti. Il faut le DIRE. Sans ce mot, une manche
                  entiere semble n'avoir servi a rien, et le joueur cherche ce
                  qu'il a rate. C'est la meme condition des deux cotes du fil :
                  personne trouve, personne elimine. */}
              {isElimination(room.settings.mode) && hunt.foundOrder.length === 0 && (
                <small className="guessing__rule" role="status">
                  {t('hunt.noElimination')}
                </small>
              )}
              {/* Legende des deux verdicts : l'icone se comprend seule, mais la
                  dire une fois evite de la deviner. */}
              <small className="reveal__legend">
                <span data-verdict="found">{t('reveal.legendFound')}</span>
                <span data-verdict="hidden">{t('reveal.legendHidden')}</span>
              </small>
            </header>
            <HuntBoard
              key={`reveal-${hunt.round}`}
              room={room}
              file={hunt.file}
              targets={hunt.targets}
              drawings={hunt.drawings}
              found={hunt.found}
              {...(isElimination(room.settings.mode) ? { foundOrder: hunt.foundOrder } : {})}
              selfId={playerId}
              frozen
              guessTimeMs={REVEAL_BROWSE_MS}
              onStrike={() => undefined}
            />
            <ReactionBar />
            <FloatingReactions room={room} />
          </div>
        </GameLayout>
      </Screen>
    );
  }

  if (reveal === null || reveal.items.length === 0) {
    return (
      <Screen name="reveal-loading">
        <p className="sandbox__status">{t('reveal.title')}</p>
      </Screen>
    );
  }

  const active = Math.min(pinned ?? index, reveal.items.length - 1);
  const item = reveal.items[active] as RevealItem;
  const drawing = reveal.drawings[item.slot] ?? null;

  return (
    <Screen name="reveal">
      <GameLayout room={room} selfId={playerId}>
        <div className="reveal">
          <header className="reveal__topbar">
            <span className="reveal__phase">{roundLabel(t, room, reveal.round)}</span>
            {/* Sans ce compte a rebours, la fin de la consultation tombait sans
                prevenir au milieu d'un tableau qu'on etait en train de regarder. */}
            <PhaseTimer
              endsAt={room.endsAt}
              durationMs={revealDurationMs(reveal.items.length)}
              size={44}
            />
          </header>

          <RevealStage
            key={`${reveal.round}-${item.slot}`}
            item={item}
            drawing={drawing}
            room={room}
          />

          <ReactionBar />
          <FloatingReactions room={room} />

          <RevealFilmstrip
            key={reveal.round}
            items={reveal.items}
            drawings={reveal.drawings}
            room={room}
            active={active}
            onSelect={setPinned}
          />
        </div>
      </GameLayout>
    </Screen>
  );
}

/** Cote des miniatures rendues hors ecran, en pixels. */
const THUMB_PX = 192;

/**
 * Pellicule des retouches de la manche : une miniature par joueur, nommee.
 * Rendue une seule fois par manche : `renderThumbnail` recharge le tableau et
 * rejoue le dessin, c'est trop cher pour une frame d'animation.
 */
function RevealFilmstrip({
  items,
  drawings,
  room,
  active,
  onSelect,
}: {
  items: readonly RevealItem[];
  drawings: Readonly<Record<number, ArrayBuffer>>;
  room: RoomSnapshot;
  active: number;
  onSelect: (index: number) => void;
}): ReactElement {
  const t = useT();
  const [thumbs, setThumbs] = useState<ReadonlyMap<number, string>>(() => new Map());
  /** slot -> le rendu tenait-il deja le blob final. */
  const rendered = useRef<Map<number, boolean>>(new Map());

  // `drawings` change d'identite a chaque blob recu. On rend donc une miniature
  // au plus deux fois par slot : une fois a vide si le blob tarde, une fois
  // pour de bon a son arrivee.
  useEffect(() => {
    let alive = true;
    for (const item of items) {
      const blob = drawings[item.slot] ?? null;
      const settled = rendered.current.get(item.slot);
      if (settled === true) continue;
      if (settled === false && blob === null) continue;
      rendered.current.set(item.slot, blob !== null);

      void renderThumbnail(
        { file: item.painting.file, zone: item.zone, palette: item.palette, drawing: blob },
        THUMB_PX,
      )
        // Une miniature qui echoue n'empeche pas les autres de s'afficher.
        .then((url) => {
          if (!alive) return;
          setThumbs((current) => new Map(current).set(item.slot, url));
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [items, drawings]);

  return (
    <div className="filmstrip" role="tablist" aria-label={t('reveal.everyone')}>
      {items.map((item, i) => {
        const drawer = playerById(room, item.drawerId);
        const url = thumbs.get(item.slot);
        return (
          <button
            key={item.slot}
            type="button"
            role="tab"
            aria-selected={i === active}
            className="filmstrip__item"
            data-active={i === active}
            onClick={() => onSelect(i)}
          >
            <span className="filmstrip__frame">
              {url !== undefined && <img className="filmstrip__thumb" src={url} alt="" />}
            </span>
            <span className="filmstrip__name">{drawer?.nickname ?? '?'}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Un tableau revele : canvas pilote par RevealEngine, poignee, verdict. */
function RevealStage({
  item,
  drawing,
  room,
}: {
  item: RevealItem;
  drawing: ArrayBuffer | null;
  room: RoomSnapshot;
}): ReactElement {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<RevealEngine | null>(null);

  const drawer = playerById(room, item.drawerId);
  const guesser = playerById(room, item.guesserId);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    let alive = true;
    let engine: RevealEngine | null = null;

    void loadArtwork(item.painting.file)
      .then((image) => {
        if (!alive) return;

        let strokes: ReturnType<typeof decodeDrawing> = [];
        if (drawing !== null && drawing.byteLength > 0) {
          try {
            strokes = decodeDrawing(drawing);
          } catch {
            // Blob illisible : on montre la zone vide plutot que rien.
          }
        }

        engine = new RevealEngine({
          canvas,
          image,
          zone: item.zone,
          drawing: strokes,
          palette: item.palette,
          introMs: prefersReducedMotion() ? 0 : INTRO_MS,
          autoSlideMs: prefersReducedMotion() ? 0 : AUTO_SLIDE_MS,
        });
        engineRef.current = engine;

        // La poignee suit le trait dore. Un transform, pas un `left` : c'est la
        // seule ecriture DOM de la boucle, elle ne doit rien faire refluer.
        engine.onSplit(() => {
          const node = handleRef.current;
          if (node !== null) node.style.transform = `translateX(${engine?.splitPx ?? 0}px)`;
        });
        engine.start();
      })
      .catch(() => undefined);

    // La taille du canvas change aussi SANS evenement de fenetre : on observe
    // la boite elle-meme, sinon le rendu garde une geometrie perimee.
    const stopResize = observeSize(canvas, () => engineRef.current?.resize());

    return () => {
      alive = false;
      stopResize();
      engine?.destroy();
      engineRef.current = null;
    };
  }, [item, drawing]);

  const onHandleDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    // Le canvas ne doit surtout pas voir ce geste : il le prendrait pour un pan.
    event.stopPropagation();
    const bounds = event.currentTarget.parentElement?.getBoundingClientRect();
    if (bounds === undefined) return;
    engineRef.current?.setSplit(
      engineRef.current.splitFromView(event.clientX - bounds.left),
    );
  };

  const onHandleMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.buttons === 0 && event.pointerType === 'mouse') return;
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    event.stopPropagation();
    const bounds = event.currentTarget.parentElement?.getBoundingClientRect();
    if (bounds === undefined) return;
    engineRef.current?.setSplit(
      engineRef.current.splitFromView(event.clientX - bounds.left),
    );
  };

  return (
    <div className="reveal__stage">
      <div className="reveal__canvas-wrap">
        <canvas ref={canvasRef} className="reveal__canvas" />
        <div
          ref={handleRef}
          className="reveal__split-handle"
          role="separator"
          aria-label={t('reveal.compare')}
          style={{ width: SPLIT_GRAB_PX * 2, marginLeft: -SPLIT_GRAB_PX }}
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
        />
        <div className="reveal__labels" aria-hidden="true">
          <span>{t('reveal.retouched')}</span>
          <span>{t('reveal.original')}</span>
        </div>
      </div>

      <div className="reveal__info">
        <div className="reveal__party">
          {drawer !== null && <Avatar playerId={drawer.id} avatar={drawer.avatar} colorIndex={drawer.colorIndex} size={28} />}
          <span>{t('reveal.drawnBy', { name: drawer?.nickname ?? '?' })}</span>
        </div>
        <div className="reveal__party">
          <span data-found={item.found} className="reveal__verdict">
            {item.found ? t('reveal.found') : t('reveal.notFound')}
          </span>
          <span className="reveal__guesser">
            {t('reveal.guessedBy', { name: guesser?.nickname ?? t('reveal.nobody') })}
          </span>
        </div>
        <div className="reveal__points mono-tabular">
          <span>
            +{item.drawerPoints} <small>{t('common.drawer')}</small>
          </span>
          <span>
            +{item.guesserPoints} <small>{t('common.guesser')}</small>
          </span>
        </div>
      </div>
    </div>
  );
}

function ReactionBar(): ReactElement {
  return (
    <div className="reaction-bar">
      {REACTIONS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className="reaction-bar__button"
          onClick={() => connection.send({ type: 'reaction', emoji })}
          aria-label={emoji}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

/**
 * Emoji flottants, un par reaction recue, retires a la fin de l'animation.
 *
 * Ils PARTENT DU PSEUDO de celui qui les envoie, dans le rail des scores, et
 * derivent vers la droite. Ils montaient auparavant du bas de l'ecran, a une
 * abscisse tiree de la couleur du joueur : rien ne disait qui avait reagi, et
 * il fallait connaitre par coeur la couleur de chacun pour le deviner.
 */
function FloatingReactions({ room }: { room: RoomSnapshot }): ReactElement {
  const reactions = useRoomStore((state) => state.reactions);
  const drop = useRoomStore((state) => state.dropReaction);

  useEffect(() => {
    if (reactions.length === 0) return;
    const timers = reactions.map((reaction) =>
      window.setTimeout(() => drop(reaction.id), 1600),
    );
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [reactions, drop]);

  return (
    <div className="floating-reactions" aria-hidden="true">
      {reactions.map((reaction) => (
        <FloatingReaction
          key={reaction.id}
          emoji={reaction.emoji}
          playerId={reaction.playerId}
          rank={room.players.findIndex((player) => player.id === reaction.playerId)}
        />
      ))}
    </div>
  );
}

/** Hauteur d'une ligne du rail, pour le repli quand il n'est pas a l'ecran. */
const ROW_HEIGHT = 34;

function FloatingReaction({
  emoji,
  playerId,
  rank,
}: {
  readonly emoji: string;
  readonly playerId: string;
  readonly rank: number;
}): ReactElement {
  /**
   * L'ancre est mesuree UNE FOIS, au montage.
   *
   * La ligne du rail porte deja `data-player-id` (elle en a besoin pour son
   * animation d'echange de place) : on lit sa boite plutot que de dupliquer sa
   * position dans une constante qui se desynchroniserait au premier changement
   * de mise en page. Mesurer a chaque image ferait relire la mise en page
   * soixante fois par seconde pour un emoji qui ne bouge pas de son point de
   * depart.
   */
  const [anchor] = useState(() => {
    const row = document.querySelector(`[data-player-id="${CSS.escape(playerId)}"]`);
    if (row !== null) {
      const box = row.getBoundingClientRect();
      // Au BORD DROIT de la ligne : l'emoji part du pseudo, il ne le recouvre
      // pas.
      return { top: box.top + box.height / 2, left: box.right };
    }
    // Rail absent (tiroir ferme sur ecran etroit) : on se cale sur le rang du
    // joueur, ce qui garde des departs distincts d'un joueur a l'autre.
    return { top: 96 + Math.max(rank, 0) * ROW_HEIGHT, left: 12 };
  });

  return (
    <span
      className="floating-reactions__item"
      style={{ top: `${Math.round(anchor.top)}px`, left: `${Math.round(anchor.left)}px` }}
    >
      {emoji}
    </span>
  );
}
